/**
 * Chamadas HTTP da agenda de oficinas.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/oficinas` que a tela usa —
 * listar, criar, alterar e excluir uma oficina.
 *
 * Como usar: a listagem devolve o array inteiro, em ordem alfabética. Nenhuma
 * transformação acontece aqui — o telefone sai já reduzido a dígitos pelo
 * schema.
 */
import { type NovaOficina, type Oficina, oficinaSchema } from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeOficinasSchema = z.array(oficinaSchema);

/**
 * Lê todas as oficinas, em ordem alfabética de nome.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As oficinas cadastradas, possivelmente nenhuma.
 */
export function buscarOficinas(sinal?: AbortSignal): Promise<Oficina[]> {
  return clienteApi.get('/oficinas', { schema: listaDeOficinasSchema, sinal });
}

/**
 * Cria uma oficina na agenda.
 *
 * @param dados - A oficina preenchida, já validada por `novaOficinaSchema`.
 * @returns A oficina como ficou gravada.
 */
export function criarOficina(dados: NovaOficina): Promise<Oficina> {
  return clienteApi.post('/oficinas', dados, { schema: oficinaSchema });
}

/**
 * Substitui todos os campos de uma oficina.
 *
 * @param id - Identificador da linha em `contatos_veiculo`.
 * @param dados - A oficina inteira, e não apenas os campos alterados.
 * @returns A oficina como ficou gravada.
 */
export function atualizarOficina(id: string, dados: NovaOficina): Promise<Oficina> {
  return clienteApi.put(`/oficinas/${encodeURIComponent(id)}`, dados, {
    schema: oficinaSchema,
  });
}

/**
 * Apaga uma oficina da agenda.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `contatos_veiculo`.
 */
export function excluirOficina(id: string): Promise<void> {
  return clienteApi.remover(`/oficinas/${encodeURIComponent(id)}`);
}
